import React, { ReactNode, HTMLAttributes } from 'react';
import styled, { CSSProperties } from 'styled-components';
import constants from '../common/theme/constants';
import { TranslateFunc } from '../common/types';

const defaultSize = '1rem';

const IconContainer = styled.div<{ size?: string; fill?: string }>`
  display: inline-block;
  width: ${({ size }) => size ?? defaultSize};
  height: ${({ size }) => size ?? defaultSize};
  max-width: ${({ size }) => size ?? defaultSize};
  min-width: ${({ size }) => size ?? defaultSize};
`;

const IconSVG = styled.svg`
  width: 100%;
  height: 100%;
  transition: all ${constants.duration.short}s cubic-bezier(0.445, 0.05, 0.55, 0.95);
  fill: ${({ fill, theme }) => fill || theme.icon};
`;

const iconShapes = {
  angle: <path d='M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z' />,
  caret: <path d='M7,10L12,15L17,10H7Z' />,
};

// SVG grabbed from here:
// https://materialdesignicons.com/
const iconMap: Record<
  string,
  { shape: ReactNode; transform?: string; translationKey?: string; viewBox?: string }
> = {
  'alert-circle-outline': {
    shape: (
      <path d='M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z' />
    ),
    translationKey: 'kit:icons.error',
  },
  'angle-left': {
    shape: iconShapes.angle,
    translationKey: 'kit:icons.angle_left',
    viewBox: '4 4 16 16',
  },
  'angle-right': {
    shape: iconShapes.angle,
    transform: 'rotate(180deg)',
    translationKey: 'kit:icons.angle_right',
    viewBox: '4 4 16 16',
  },
  'angle-up': {
    shape: iconShapes.angle,
    transform: 'rotate(90deg)',
    translationKey: 'kit:icons.angle_up',
    viewBox: '6 4 12 16',
  },
  'angle-down': {
    shape: iconShapes.angle,
    transform: 'rotate(-90deg)',
    translationKey: 'kit:icons.angle_down',
    viewBox: '4 4 16 16',
  },
  'arrow-left': {
    shape: <path d='M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z' />,
    translationKey: 'kit:icons.arrow_left',
  },
  'caret-up': {
    shape: iconShapes.caret,
    transform: 'rotate(-180deg)',
    translationKey: 'kit:icons.caret_up',
    viewBox: '4 4 16 16',
  },
  'caret-down': {
    shape: iconShapes.caret,
    translationKey: 'kit:icons.caret_down',
    viewBox: '4 4 16 16',
  },
  charging: {
    shape: (
      <path d='M12 2.02c-5.51 0-9.98 4.47-9.98 9.98s4.47 9.98 9.98 9.98 9.98-4.47 9.98-9.98S17.51 2.02 12 2.02zM11.48 20v-6.26H8L13 4v6.26h3.35L11.48 20z' />
    ),
    translationKey: 'kit:icons.charging',
  },
  check: {
    shape: <path d='M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z' />,
    translationKey: 'kit:icons.check',
  },
  'check-bold': {
    shape: <path d='M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z' />,
    translationKey: 'kit:icons.check_bold',
  },
  'check-circle': {
    shape: (
      <path d='M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z' />
    ),
    translationKey: 'kit:icons.check_circle',
  },
  close: {
    shape: (
      <path d='M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z' />
    ),
    translationKey: 'kit:icons.close',
  },
  'close-circle': {
    shape: (
      <path d='M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z' />
    ),
    translationKey: 'kit:icons.close_circle',
  },
  collapse: {
    shape: (
      <path d='M19.5,3.09L15,7.59V4H13V11H20V9H16.41L20.91,4.5L19.5,3.09M4,13V15H7.59L3.09,19.5L4.5,20.91L9,16.41V20H11V13H4Z' />
    ),
    translationKey: 'kit:icons.collapse',
  },
  'ellipsis-horizontal': {
    shape: (
      <path d='M16,12A2,2 0 0,1 18,10A2,2 0 0,1 20,12A2,2 0 0,1 18,14A2,2 0 0,1 16,12M10,12A2,2 0 0,1 12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12M4,12A2,2 0 0,1 6,10A2,2 0 0,1 8,12A2,2 0 0,1 6,14A2,2 0 0,1 4,12Z' />
    ),
    translationKey: 'kit:icons.ellipsis_horizontal',
  },
  'ellipsis-vertical': {
    shape: (
      <path d='M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z' />
    ),
    translationKey: 'kit:icons.ellipsis_vertical',
  },
  email: {
    shape: (
      <path d='M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6M20 6L12 11L4 6H20M20 18H4V8L12 13L20 8V18Z' />
    ),
    translationKey: 'kit:icons.email',
  },
  expand: {
    shape: (
      <path d='M10,21V19H6.41L10.91,14.5L9.5,13.09L5,17.59V14H3V21H10M14.5,10.91L19,6.41V10H21V3H14V5H17.59L13.09,9.5L14.5,10.91Z' />
    ),
    translationKey: 'kit:icons.expand',
  },
  fullscreen: {
    shape: (
      <path d='M5,5H10V7H7V10H5V5M14,5H19V10H17V7H14V5M17,14H19V19H14V17H17V14M10,17V19H5V14H7V17H10Z' />
    ),
    translationKey: 'kit:icons.full_screen',
  },
  'fullscreen-exit': {
    shape: (
      <path d='M14,14H19V16H16V19H14V14M5,14H10V19H8V16H5V14M8,5H10V10H5V8H8V5M19,8V10H14V5H16V8H19Z' />
    ),
    translationKey: 'kit:icons.full_screen_exit',
  },
  home: {
    shape: <path d='M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z' />,
    translationKey: 'kit:icons.home',
  },
  'information-outline': {
    shape: (
      <path d='M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z' />
    ),
    translationKey: 'kit:icons.information_outline',
  },
  'electrical-services': {
    shape: (
      <g xmlns='http://www.w3.org/2000/svg'>
        <path d='M21,14c0-0.55-0.45-1-1-1h-2v2h2C20.55,15,21,14.55,21,14z' />
        <path d='M20,17h-2v2h2c0.55,0,1-0.45,1-1C21,17.45,20.55,17,20,17z' />
        <path d='M12,14h-2v4h2c0,1.1,0.9,2,2,2h3v-8h-3C12.9,12,12,12.9,12,14z' />
        <path d='M5,13c0-1.1,0.9-2,2-2h1.5c1.93,0,3.5-1.57,3.5-3.5S10.43,4,8.5,4H5C4.45,4,4,4.45,4,5c0,0.55,0.45,1,1,1h3.5 C9.33,6,10,6.67,10,7.5S9.33,9,8.5,9H7c-2.21,0-4,1.79-4,4c0,2.21,1.79,4,4,4h2v-2H7C5.9,15,5,14.1,5,13z' />
      </g>
    ),
    translationKey: 'kit:icons.electrical_services',
  },
  'favorite-border': {
    shape: (
      <path d='M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05z' />
    ),
    translationKey: 'kit:icons.favorite_heart',
  },
  filter: {
    shape: (
      <path d='M14,12V19.88C14.04,20.18 13.94,20.5 13.71,20.71C13.32,21.1 12.69,21.1 12.3,20.71L10.29,18.7C10.06,18.47 9.96,18.16 10,17.87V12H9.97L4.21,4.62C3.87,4.19 3.95,3.56 4.38,3.22C4.57,3.08 4.78,3 5,3V3H19V3C19.22,3 19.43,3.08 19.62,3.22C20.05,3.56 20.13,4.19 19.79,4.62L14.03,12H14Z' />
    ),
    translationKey: 'kit:icons.filter',
  },
  'filter-outline': {
    shape: (
      <path d='M15,19.88C15.04,20.18 14.94,20.5 14.71,20.71C14.32,21.1 13.69,21.1 13.3,20.71L9.29,16.7C9.06,16.47 8.96,16.16 9,15.87V10.75L4.21,4.62C3.87,4.19 3.95,3.56 4.38,3.22C4.57,3.08 4.78,3 5,3V3H19V3C19.22,3 19.43,3.08 19.62,3.22C20.05,3.56 20.13,4.19 19.79,4.62L15,10.75V19.88M7.04,5L11,10.06V15.58L13,17.58V10.05L16.96,5H7.04Z' />
    ),
    translationKey: 'kit:icons.filter_outline',
  },
  magnify: {
    shape: (
      <path d='M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z' />
    ),
    translationKey: 'kit:icons.magnify',
  },
  menu: {
    shape: <path d='M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z' />,
    translationKey: 'kit:icons.menu',
  },
  'map-marker': {
    shape: (
      <path d='M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z' />
    ),
    translationKey: 'kit:icons.map_marker',
  },
  'map-outline': {
    shape: (
      <path d='M20.5,3L20.34,3.03L15,5.1L9,3L3.36,4.9C3.15,4.97 3,5.15 3,5.38V20.5A0.5,0.5 0 0,0 3.5,21L3.66,20.97L9,18.9L15,21L20.64,19.1C20.85,19.03 21,18.85 21,18.62V3.5A0.5,0.5 0 0,0 20.5,3M10,5.47L14,6.87V18.53L10,17.13V5.47M5,6.46L8,5.45V17.15L5,18.31V6.46M19,17.54L16,18.55V6.86L19,5.7V17.54Z' />
    ),
    translationKey: 'kit:icons.map_outline',
  },
  'message-reply-text': {
    shape: (
      <path d='M18,8H6V6H18V8M18,11H6V9H18V11M18,14H6V12H18V14M22,4A2,2 0 0,0 20,2H4A2,2 0 0,0 2,4V16A2,2 0 0,0 4,18H18L22,22V4Z' />
    ),
    translationKey: 'kit:icons.message_reply_text',
  },
  plus: {
    shape: <path d='M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z' />,
    viewBox: '4 4 16 16',
    translationKey: 'kit:icons.plus',
  },
  minus: {
    shape: <path d='M19,13H5V11H19V13Z' />,
    viewBox: '4 4 16 16',
    translationKey: 'kit:icons.minus',
  },
  schedule: {
    shape: (
      <g xmlns='http://www.w3.org/2000/svg'>
        <path d='M0 0h24v24H0z' fill='none' />
        <path d='M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z' />
        <path d='M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z' />
      </g>
    ),
    translationKey: 'kit:icons.schedule',
  },
  table: {
    shape: (
      <path d='M4,3H20A2,2 0 0,1 22,5V20A2,2 0 0,1 20,22H4A2,2 0 0,1 2,20V5A2,2 0 0,1 4,3M4,7V10H8V7H4M10,7V10H14V7H10M20,10V7H16V10H20M4,12V15H8V12H4M4,20H8V17H4V20M10,12V15H14V12H10M10,20H14V17H10V20M20,20V17H16V20H20M20,12H16V15H20V12Z' />
    ),
    translationKey: 'kit:icons.table',
  },
  warning: {
    shape: <path d='M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16' />,
    translationKey: 'kit:icons.warning',
  },
  'theme-dark': {
    shape: (
      <path d='M17.75,4.09L15.22,6.03L16.13,9.09L13.5,7.28L10.87,9.09L11.78,6.03L9.25,4.09L12.44,4L13.5,1L14.56,4L17.75,4.09M21.25,11L19.61,12.25L20.2,14.23L18.5,13.06L16.8,14.23L17.39,12.25L15.75,11L17.81,10.95L18.5,9L19.19,10.95L21.25,11M18.97,15.95C19.8,15.87 20.69,17.05 20.16,17.8C19.84,18.25 19.5,18.67 19.08,19.07C15.17,23 8.84,23 4.94,19.07C1.03,15.17 1.03,8.83 4.94,4.93C5.34,4.53 5.76,4.17 6.21,3.85C6.96,3.32 8.14,4.21 8.06,5.04C7.79,7.9 8.75,10.87 10.95,13.06C13.14,15.26 16.1,16.22 18.97,15.95M17.33,17.97C14.5,17.81 11.7,16.64 9.53,14.5C7.36,12.31 6.2,9.5 6.04,6.68C3.23,9.82 3.34,14.64 6.35,17.66C9.37,20.67 14.19,20.78 17.33,17.97Z' />
    ),
    translationKey: 'kit:icons.theme_dark',
  },
  'theme-light': {
    shape: (
      <path d='M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,2L14.39,5.42C13.65,5.15 12.84,5 12,5C11.16,5 10.35,5.15 9.61,5.42L12,2M3.34,7L7.5,6.65C6.9,7.16 6.36,7.78 5.94,8.5C5.5,9.24 5.25,10 5.11,10.79L3.34,7M3.36,17L5.12,13.23C5.26,14 5.53,14.78 5.95,15.5C6.37,16.24 6.91,16.86 7.5,17.37L3.36,17M20.65,7L18.88,10.79C18.74,10 18.47,9.23 18.05,8.5C17.63,7.78 17.1,7.15 16.5,6.64L20.65,7M20.64,17L16.5,17.36C17.09,16.85 17.62,16.22 18.04,15.5C18.46,14.77 18.73,14 18.87,13.21L20.64,17M12,22L9.59,18.56C10.33,18.83 11.14,19 12,19C12.82,19 13.63,18.83 14.37,18.56L12,22Z' />
    ),
    translationKey: 'kit:icons.theme_dark',
  },
  web: {
    shape: (
      <path d='M16.36,14C16.44,13.34 16.5,12.68 16.5,12C16.5,11.32 16.44,10.66 16.36,10H19.74C19.9,10.64 20,11.31 20,12C20,12.69 19.9,13.36 19.74,14M14.59,19.56C15.19,18.45 15.65,17.25 15.97,16H18.92C17.96,17.65 16.43,18.93 14.59,19.56M14.34,14H9.66C9.56,13.34 9.5,12.68 9.5,12C9.5,11.32 9.56,10.65 9.66,10H14.34C14.43,10.65 14.5,11.32 14.5,12C14.5,12.68 14.43,13.34 14.34,14M12,19.96C11.17,18.76 10.5,17.43 10.09,16H13.91C13.5,17.43 12.83,18.76 12,19.96M8,8H5.08C6.03,6.34 7.57,5.06 9.4,4.44C8.8,5.55 8.35,6.75 8,8M5.08,16H8C8.35,17.25 8.8,18.45 9.4,19.56C7.57,18.93 6.03,17.65 5.08,16M4.26,14C4.1,13.36 4,12.69 4,12C4,11.31 4.1,10.64 4.26,10H7.64C7.56,10.66 7.5,11.32 7.5,12C7.5,12.68 7.56,13.34 7.64,14M12,4.03C12.83,5.23 13.5,6.57 13.91,8H10.09C10.5,6.57 11.17,5.23 12,4.03M18.92,8H15.97C15.65,6.75 15.19,5.55 14.59,4.44C16.43,5.07 17.96,6.34 18.92,8M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z' />
    ),
    translationKey: 'kit:icons.web',
  },
  list: {
    shape: (
      <path d='M7,5H21V7H7V5M7,13V11H21V13H7M4,4.5A1.5,1.5 0 0,1 5.5,6A1.5,1.5 0 0,1 4,7.5A1.5,1.5 0 0,1 2.5,6A1.5,1.5 0 0,1 4,4.5M4,10.5A1.5,1.5 0 0,1 5.5,12A1.5,1.5 0 0,1 4,13.5A1.5,1.5 0 0,1 2.5,12A1.5,1.5 0 0,1 4,10.5M7,19V17H21V19H7M4,16.5A1.5,1.5 0 0,1 5.5,18A1.5,1.5 0 0,1 4,19.5A1.5,1.5 0 0,1 2.5,18A1.5,1.5 0 0,1 4,16.5Z' />
    ),
    translationKey: 'kit:icons.list',
  },
  gps: {
    shape: (
      <path d='M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M3.05,13H1V11H3.05C3.5,6.83 6.83,3.5 11,3.05V1H13V3.05C17.17,3.5 20.5,6.83 20.95,11H23V13H20.95C20.5,17.17 17.17,20.5 13,20.95V23H11V20.95C6.83,20.5 3.5,17.17 3.05,13M12,5A7,7 0 0,0 5,12A7,7 0 0,0 12,19A7,7 0 0,0 19,12A7,7 0 0,0 12,5Z' />
    ),
    translationKey: 'kit:icons.gps',
  },
  car: {
    shape: (
      <path d='M5,11L6.5,6.5H17.5L19,11M17.5,16A1.5,1.5 0 0,1 16,14.5A1.5,1.5 0 0,1 17.5,13A1.5,1.5 0 0,1 19,14.5A1.5,1.5 0 0,1 17.5,16M6.5,16A1.5,1.5 0 0,1 5,14.5A1.5,1.5 0 0,1 6.5,13A1.5,1.5 0 0,1 8,14.5A1.5,1.5 0 0,1 6.5,16M18.92,6C18.72,5.42 18.16,5 17.5,5H6.5C5.84,5 5.28,5.42 5.08,6L3,12V20A1,1 0 0,0 4,21H5A1,1 0 0,0 6,20V19H18V20A1,1 0 0,0 19,21H20A1,1 0 0,0 21,20V12L18.92,6Z' />
    ),
    translationKey: 'kit:icons.car',
  },
  'tray-arrow-up': {
    shape: (
      <path d='M2 12H4V17H20V12H22V17C22 18.11 21.11 19 20 19H4C2.9 19 2 18.11 2 17V12M12 2L6.46 7.46L7.88 8.88L11 5.75V15H13V5.75L16.13 8.88L17.55 7.45L12 2Z' />
    ),
    translationKey: 'kit:icons.tray_arrow_up',
  },
  'share': {
    shape: (
      <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z"/>
    ),
    translationKey: 'kit:icons.share',
  },
  cash: {
    shape: (
      <path d='M5,6H23V18H5V6M14,9A3,3 0 0,1 17,12A3,3 0 0,1 14,15A3,3 0 0,1 11,12A3,3 0 0,1 14,9M9,8A2,2 0 0,1 7,10V14A2,2 0 0,1 9,16H19A2,2 0 0,1 21,14V10A2,2 0 0,1 19,8H9M1,10H3V20H19V22H1V10Z' />
    ),
    translationKey: 'kit:icons.cash',
  },
  'trending-up': {
    shape: (
      <path d='M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z' />
    ),
    translationKey: 'kit:icons.trending_up',
  },
  'camera-plus': {
    shape: (
      <path d='M3 4V1H5V4H8V6H5V9H3V6H0V4M6 10V7H9V4H16L17.8 6H21C22.1 6 23 6.9 23 8V20C23 21.1 22.1 22 21 22H5C3.9 22 3 21.1 3 20V10M13 19C17.45 19 19.69 13.62 16.54 10.46C13.39 7.31 8 9.55 8 14C8 16.76 10.24 19 13 19M9.8 14C9.8 16.85 13.25 18.28 15.26 16.26C17.28 14.25 15.85 10.8 13 10.8C11.24 10.8 9.8 12.24 9.8 14Z' />
    ),
    translationKey: 'kit:icons.camera_plus',
  },
  'image-filter-center-focus-strong': {
    shape: (
      <path d='M12 8C8.44 8 6.65 12.31 9.17 14.83C11.69 17.35 16 15.56 16 12C16 9.79 14.21 8 12 8M5 15H3V19C3 20.1 3.9 21 5 21H9V19H5M5 5H9V3H5C3.9 3 3 3.9 3 5V9H5M19 3H15V5H19V9H21V5C21 3.9 20.1 3 19 3M19 19H15V21H19C20.1 21 21 20.1 21 19V15H19' />
    ),
    translationKey: 'kit:image-filter-center-focus-strong',
  },
  trash: {
    shape: (
      <path d='M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z' />
    ),
    translationKey: 'kit:icons.trash',
  },
  accessible: {
    shape: (
      <>
        <path d='M0 0h24v24H0z' fill='none' />
        <circle cx='17' cy='4.54' r='2' />
        <path d='M14 17h-2c0 1.65-1.35 3-3 3s-3-1.35-3-3 1.35-3 3-3v-2c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5zm3-3.5h-1.86l1.67-3.67C17.42 8.5 16.44 7 14.96 7h-5.2c-.81 0-1.54.47-1.87 1.2L7.22 10l1.92.53L9.79 9H12l-1.83 4.1c-.6 1.33.39 2.9 1.85 2.9H17v5h2v-5.5c0-1.1-.9-2-2-2z' />
      </>
    ),
    translationKey: 'kit:icons.accessible',
  },
};

export const iconTypes: string[] = Object.keys(iconMap);

export interface KitIconProps {
  'aria-label'?: string;
  className?: string;
  icon: string;
  fill?: string;
  /**
   * Font size in rem.
   * @default 1
   */
  size?: string;
  viewBox?: string;
  title?: string;
  t?: TranslateFunc;
  as?: 'div' | 'span';
}

const KitIcon: React.FC<KitIconProps & HTMLAttributes<HTMLOrSVGElement>> = ({
  icon,
  size,
  fill,
  className,
  title,
  role,
  t,
  as,
  ...rest
}) => {
  const { shape, transform, translationKey, viewBox } = iconMap[icon];
  const style: CSSProperties = {};

  if (transform) {
    style.transform = transform;
  }

  let iconTitle = title;
  if (!iconTitle && t && translationKey) {
    iconTitle = t(translationKey);
  }

  const svgViewBox: string = viewBox ?? '0 0 24 24';
  return (
    <IconContainer size={size} className={className} role={role} as={as}>
      <IconSVG
        fill={fill}
        className='cp-icon'
        xmlns='http://www.w3.org/2000/svg'
        version='1.1'
        style={style}
        preserveAspectRatio='xMidYMid meet'
        viewBox={svgViewBox}
        // role='img' TODO: This should have a role of img, but we'll have to add title to everywhere so it needs a new ticket
        // See https://accessibilityinsights.io/info-examples/web/svg-img-alt/
        {...rest}
      >
        <title>{iconTitle}</title>
        {shape}
      </IconSVG>
    </IconContainer>
  );
};

export default KitIcon;
